import { useCallback } from 'react';

import { useDateTimeContext } from '@rtkwlf/fenrir-react';
import type { LabelFormatting } from '@rtkwlf/insights';
import { LabelFormattingKindEnum } from '@rtkwlf/insights';
import invariant from 'tiny-invariant';

import { truncateLabel } from '@packages/utils/base.utils';
import { formatBytes } from '@packages/utils/bytes/bytes';
import {
  dateFormatByIntervalWindow,
  DateIntervalWindowFormats,
} from '@packages/utils/date-format-by-interval-window/date-format-by-interval-window';

const dateFormatterDaily = (
  value: string | number,
  _: LabelFormatting,
  isUtc: boolean,
): string =>
  dateFormatByIntervalWindow(
    value.toString(),
    DateIntervalWindowFormats[isUtc ? 'UtcDaily' : 'Daily'],
    isUtc,
  );

const dateFormatterHourly = (
  value: string | number,
  _: LabelFormatting,
  isUtc: boolean,
): string =>
  dateFormatByIntervalWindow(
    value.toString(),
    DateIntervalWindowFormats[isUtc ? 'UtcHourly' : 'Hourly'],
    isUtc,
  );

const dateFormatterMonthly = (
  value: string | number,
  _: LabelFormatting,
  isUtc: boolean,
): string => {
  return dateFormatByIntervalWindow(
    value.toString(),
    DateIntervalWindowFormats[isUtc ? 'UtcMonthly' : 'Monthly'],
    true, // monthly bucketed items always have UTC timestamp
  );
};

const numberFormatter = (
  value: string | number,
  format: LabelFormatting,
): string => {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: format.value ?? 1,
  }).format(Number(value));
};

const stringMaxLenFormatter = (
  label: string | number,
  format: LabelFormatting,
): string => {
  if (!format.value) return label.toString();
  return truncateLabel(label.toString(), format.value);
};

function dataVolumeFormatter(
  value: string | number,
  _: LabelFormatting,
): string {
  return formatBytes(Number(value));
}

const formatterRecord: Record<
  LabelFormattingKindEnum,
  (value: string | number, format: LabelFormatting, isUtc: boolean) => string
> = {
  [LabelFormattingKindEnum.DateDaily]: dateFormatterDaily,
  [LabelFormattingKindEnum.DateHourly]: dateFormatterHourly,
  [LabelFormattingKindEnum.DateMonthly]: dateFormatterMonthly,
  [LabelFormattingKindEnum.NumberMaxDecimals]: numberFormatter,
  [LabelFormattingKindEnum.StringMaxLength]: stringMaxLenFormatter,
  [LabelFormattingKindEnum.DataVolume]: dataVolumeFormatter,
};

type UseFormatterOptions = {
  default?: LabelFormatting;
};

export const useFormatter = (
  schemaFormatter: LabelFormatting | undefined,
  options?: UseFormatterOptions,
) => {
  const { timezone } = useDateTimeContext();
  const isUtc = timezone === 'utc';
  return useCallback(
    (value: string | number): string => {
      // take the schema formatter otherwise the default takes precedence
      const selectedFormatter = schemaFormatter ?? options?.default;
      if (!selectedFormatter) return value.toString();
      const { kind } = selectedFormatter;
      invariant(kind, 'formatter should be defined');

      const formatter = formatterRecord[kind];
      return formatter(value, selectedFormatter, isUtc);
    },
    [schemaFormatter, options?.default, isUtc],
  );
};
